import React, { useState, useEffect } from 'react';
import {
  ClipboardList,
  Loader,
  AlertCircle,
  RefreshCw,
  ChevronRight,
  CheckCircle2,
  Clock
} from 'lucide-react';
import { useAuth } from '../../../../contexts/AuthContext';
import { useWarehouse } from '../../../../contexts/WarehouseContext';
import { countingListCreationService } from '../../../../services/countingListCreationService';
import { CountingListOverviewItem } from '../../../../types/countingListCreation';

interface CountingListSelectorProps {
  onSelectList: (list: CountingListOverviewItem) => void;
}

export const CountingListSelector: React.FC<CountingListSelectorProps> = ({ onSelectList }) => {
  const { user } = useAuth();
  const { currentWarehouse } = useWarehouse();
  const [lists, setLists] = useState<CountingListOverviewItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadLists = async () => {
    if (!user?.operatorId) return;
    setLoading(true);
    setError(null);
    try {
      const result = await countingListCreationService.getCountingListOverview(
        currentWarehouse || undefined,
        user.operatorId
      );
      // Only show active lists (not completed or exported): status < 50
      setLists(result.filter(l => l.orderStatus < 50));
    } catch (err) {
      console.error('Error loading counting lists:', err);
      setError('Kunne ikke laste tellelister');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadLists();
  }, [user?.operatorId, currentWarehouse]);

  const getStatusBadge = (status: number, statusText: string) => {
    const colors: Record<number, string> = {
      0: 'bg-gray-100 text-gray-700',
      10: 'bg-blue-100 text-blue-700',
      20: 'bg-yellow-100 text-yellow-700',
      40: 'bg-orange-100 text-orange-700',
      50: 'bg-green-100 text-green-700',
    };
    return (
      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${colors[status] || 'bg-gray-100 text-gray-700'}`}>
        {statusText}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <Loader className="animate-spin h-8 w-8 text-blue-500 mb-2" />
        <span className="text-sm text-gray-500">Laster tellelister...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <AlertCircle className="h-10 w-10 text-red-400 mb-3" />
        <p className="text-sm text-red-600 mb-3">{error}</p>
        <button
          onClick={loadLists}
          className="flex items-center space-x-1 px-3 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 text-sm"
        >
          <RefreshCw className="h-4 w-4" />
          <span>Prøv igjen</span>
        </button>
      </div>
    );
  }

  if (lists.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <ClipboardList className="h-10 w-10 text-gray-300 mb-3" />
        <p className="text-sm text-gray-500 mb-1">Ingen tellelister tildelt deg</p>
        <p className="text-xs text-gray-400">Kontakt lagersjef for å få tildelt en telleliste</p>
        <button
          onClick={loadLists}
          className="mt-4 flex items-center space-x-1 px-3 py-2 text-blue-600 hover:bg-blue-50 rounded text-sm"
        >
          <RefreshCw className="h-4 w-4" />
          <span>Oppdater</span>
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between mb-1">
        <h3 className="text-sm font-medium text-gray-700">
          Mine tellelister ({lists.length})
        </h3>
        <button
          onClick={loadLists}
          className="p-1.5 text-gray-500 hover:bg-gray-100 rounded"
          title="Oppdater"
        >
          <RefreshCw className="h-4 w-4" />
        </button>
      </div>

      {lists.map((list) => (
        <button
          key={list.batchID}
          onClick={() => onSelectList(list)}
          className="w-full text-left bg-white border rounded-lg p-3 hover:border-blue-300 hover:shadow-sm transition-all group"
        >
          <div className="flex items-center justify-between">
            <div className="flex-1 min-w-0">
              <div className="flex items-center space-x-2 mb-1">
                <span className="font-medium text-sm text-gray-900 truncate">
                  {list.description1 || list.batchID}
                </span>
                {getStatusBadge(list.orderStatus, list.orderStatusText)}
              </div>
              <div className="flex items-center space-x-3 text-xs text-gray-500">
                <span className="font-mono">{list.batchID}</span>
                {list.warehouse && <span>Lager: {list.warehouse}</span>}
              </div>
              {/* Progress bar */}
              <div className="mt-2">
                <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
                  <span className="flex items-center space-x-1">
                    {list.completedLines === list.totalLines && list.totalLines > 0 ? (
                      <CheckCircle2 className="h-3 w-3 text-green-500" />
                    ) : (
                      <Clock className="h-3 w-3" />
                    )}
                    <span>{list.completedLines} / {list.totalLines} linjer telt</span>
                  </span>
                  <span>{list.progressPercent}%</span>
                </div>
                <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all ${
                      list.progressPercent === 100 ? 'bg-green-500' :
                      list.progressPercent > 0 ? 'bg-blue-500' : 'bg-gray-300'
                    }`}
                    style={{ width: `${Math.max(list.progressPercent, 0)}%` }}
                  />
                </div>
              </div>
            </div>
            <ChevronRight className="h-5 w-5 text-gray-400 group-hover:text-blue-500 ml-2 flex-shrink-0" />
          </div>
        </button>
      ))}
    </div>
  );
};
